import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { from } from 'rxjs';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { CompanyService } from 'src/app/services/company.service';
import { ToastService } from 'src/app/services/toast.service';
import { companiesPublicApiService } from 'src/app/shared/vive-api/companies-public-api.service';
import { PublicApiConfigDto } from 'src/app/shared/vive-api/vive-api-models';
import { LOAD_BALANCER_TIMEOUT_MS } from 'src/app/shared/vive-api/vive-api.config';

@Component({
  selector: 'app-client-admin-public-api-keys',
  templateUrl: './client-admin-public-api-keys.component.html',
  styleUrls: ['./client-admin-public-api-keys.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ClientAdminPublicApiKeysComponent implements OnInit {
  private cdr = inject(ChangeDetectorRef);

  constructor(
    private CompanyService: CompanyService,
    private ToastService: ToastService,
    private ModalService: NgbModal,
    private route: ActivatedRoute
  ) {}

  apiKeys: PublicApiConfigDto[] = [];
  isLoading = false;
  isCreating = false;
  newlyCreatedKeyId: number | null = null;
  private companyId!: number;

  ngOnInit(): void {
    let r = this.route.snapshot;
    while (r) {
      if (r.params['CompanyId']) {
        this.companyId = Number(r.params['CompanyId']);
        break;
      }
      r = r.parent!;
    }
    this.loadKeys();
  }

  loadKeys(): void {
    this.isLoading = true;
    this.cdr.markForCheck();
    from(
      companiesPublicApiService.findAllPublicApiAccessKey({
        pathParams: { companyId: this.companyId },
        options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS }
      })
    ).subscribe({
      next: (keys) => {
        this.apiKeys = keys;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  generateKey(): void {
    this.isCreating = true;
    this.cdr.markForCheck();
    from(
      companiesPublicApiService.createPublicApiAccessKey({
        pathParams: { companyId: this.companyId },
        options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS }
      })
    ).subscribe({
      next: (key) => {
        this.apiKeys = [...this.apiKeys, key];
        this.newlyCreatedKeyId = key.id;
        this.isCreating = false;
        this.ToastService.addSuccessToast(
          'Success',
          'API key generated. Copy it now — it will not be shown again.'
        );
        this.cdr.markForCheck();
      },
      error: () => {
        this.isCreating = false;
        this.cdr.markForCheck();
      }
    });
  }

  copyKey(key: PublicApiConfigDto): void {
    navigator.clipboard.writeText(key.apiAccessKey).then(() => {
      this.ToastService.addSuccessToast(
        'Copied',
        'API key copied to clipboard.'
      );
    });
  }

  dismissNewKey(): void {
    this.newlyCreatedKeyId = null;
    this.cdr.markForCheck();
    this.loadKeys();
  }

  confirmDelete(key: PublicApiConfigDto): void {
    const modalRef = this.ModalService.open(ConfirmModalComponent, {
      centered: true
    });
    modalRef.componentInstance.displayText =
      'Are you sure you want to delete this API key? This action cannot be undone and any integrations using this key will stop working.';
    modalRef.componentInstance.continueButtonText = 'Delete';

    modalRef.closed.subscribe({
      next: (confirmed: boolean) => {
        if (confirmed) {
          this.deleteKey(key);
        }
      }
    });
  }

  // Still hand-written: the generated deletePublicApiAccessKey names its path
  // parameter `apiAccessKey` (the secret key itself), but the route takes the
  // key's numeric id.
  private deleteKey(key: PublicApiConfigDto): void {
    this.CompanyService.deletePublicApiAccessKey(
      this.companyId,
      key.id
    ).subscribe({
      next: () => {
        this.apiKeys = this.apiKeys.filter((k) => k.id !== key.id);
        if (this.newlyCreatedKeyId === key.id) {
          this.newlyCreatedKeyId = null;
        }
        this.ToastService.addSuccessToast('Success', 'API key deleted.');
        this.cdr.markForCheck();
      }
    });
  }
}
